import { matchPath } from 'react-router-dom';

import {
    type SidebarItemConfig,
    type SidebarLevel,
    sidebarLevels,
    type VisibilityConfig,
    type VisibilityRules,
} from '@/components/Layout/AppSidebar/sidebarConfig';
import type { PostHogFeatureFlag } from '@/config/availableFeatureFlags';

import { buildFallbackPageContextFromSuffix, resolveAnalyticsSectionLabel } from './sectionLabels';

export interface PageContext {
    /** Deepest matched sidebar item id (e.g. cage-weight-growth). */
    section_id: string;
    /** Parent module sidebar id (e.g. cage-weight); equals section_id for top-level items. */
    module_id: string;
    main_section: string;
    sub_page: string;
    /** Sidebar hierarchy level when the URL matches a known navigation scope. */
    navigation_level?: SidebarLevel;
    feature_flags: PostHogFeatureFlag[];
}

const EMPTY_PAGE_CONTEXT: PageContext = {
    section_id: '',
    module_id: '',
    main_section: '',
    sub_page: '',
    feature_flags: [],
};

function extractFeatureFlags(visibility?: VisibilityConfig): PostHogFeatureFlag[] {
    if (!visibility) {
        return [];
    }

    const rules: VisibilityRules[] = Array.isArray(visibility) ? visibility : [visibility];
    const flags = rules.flatMap((rule) => rule.requiredFeatureFlags ?? []);

    return [...new Set(flags)];
}

function normalizePathSuffix(suffix: string): string {
    if (!suffix || suffix === '/') {
        return '';
    }

    return suffix.startsWith('/') ? suffix : `/${suffix}`;
}

function pathMatchesItem(suffix: string, item: SidebarItemConfig): boolean {
    const itemPath = normalizePathSuffix(item.path);

    if (item.exact) {
        return suffix === itemPath || (itemPath === '' && (suffix === '' || suffix === '/'));
    }

    if (suffix === itemPath || suffix.startsWith(`${itemPath}/`)) {
        return true;
    }

    if (item.activePathPrefixes?.length) {
        return item.activePathPrefixes.some((prefix) => {
            const normalizedPrefix = normalizePathSuffix(prefix);
            return suffix === normalizedPrefix || suffix.startsWith(`${normalizedPrefix}/`);
        });
    }

    return itemPath !== '' && suffix.startsWith(itemPath);
}

function getItemDepth(item: SidebarItemConfig): number {
    if (item.path === '') {
        return 0;
    }

    return item.path.split('/').filter(Boolean).length;
}

interface MatchResult {
    parent: SidebarItemConfig;
    matched: SidebarItemConfig;
    depth: number;
}

function isMoreSpecificMatch(candidate: MatchResult, current: MatchResult | null): boolean {
    if (!current) {
        return true;
    }

    if (candidate.depth > current.depth) {
        return true;
    }

    if (candidate.depth < current.depth) {
        return false;
    }

    const candidateIsNested = candidate.parent.id !== candidate.matched.id;
    const currentIsNested = current.parent.id !== current.matched.id;

    return candidateIsNested && !currentIsNested;
}

function findDeepestItemMatch(
    suffix: string,
    item: SidebarItemConfig,
    parent: SidebarItemConfig
): MatchResult | null {
    let best: MatchResult | null = null;

    if (pathMatchesItem(suffix, item)) {
        best = {
            parent,
            matched: item,
            depth: getItemDepth(item),
        };
    }

    for (const child of item.children ?? []) {
        const childMatch = findDeepestItemMatch(suffix, child, item);
        if (childMatch && isMoreSpecificMatch(childMatch, best)) {
            best = childMatch;
        }
    }

    return best;
}

function findBestSidebarMatch(suffix: string, items: SidebarItemConfig[]): MatchResult | null {
    let best: MatchResult | null = null;

    for (const item of items) {
        const match = findDeepestItemMatch(suffix, item, item);
        if (match && isMoreSpecificMatch(match, best)) {
            best = match;
        }
    }

    return best;
}

function buildPageContext(match: MatchResult): PageContext {
    const isNested = match.parent.id !== match.matched.id;
    const mainItem = isNested ? match.parent : match.matched;
    const subItem = isNested ? match.matched : undefined;
    const mainFlags = extractFeatureFlags(mainItem.visibility);
    const subFlags = subItem ? extractFeatureFlags(subItem.visibility) : [];

    const mainSection = resolveAnalyticsSectionLabel(mainItem);
    const subPage = subItem ? resolveAnalyticsSectionLabel(subItem) : mainSection;

    return {
        section_id: match.matched.id,
        module_id: mainItem.id,
        main_section: mainSection,
        sub_page: subPage,
        feature_flags: [...new Set([...mainFlags, ...subFlags])],
    };
}

export interface SidebarPageMatch {
    level: SidebarLevel;
    /** Top-level sidebar item the page belongs to. */
    moduleItem: SidebarItemConfig;
    /** Nested item when the page is a sub-page of the module. */
    subItem?: SidebarItemConfig;
}

interface LevelMatch {
    level: SidebarLevel;
    suffix: string;
    sidebarMatch: MatchResult | null;
}

function matchLevel(pathname: string): LevelMatch | null {
    for (const level of sidebarLevels) {
        const levelMatch = matchPath({ path: level.matchPattern, end: false }, pathname);

        if (!levelMatch) {
            continue;
        }

        const suffix = normalizePathSuffix(pathname.replace(levelMatch.pathname, ''));

        return {
            level: level.level,
            suffix,
            sidebarMatch: findBestSidebarMatch(suffix, level.items),
        };
    }

    return null;
}

/** Resolves the sidebar items a URL pathname belongs to, or null when it has none. */
export function resolveSidebarMatch(pathname: string): SidebarPageMatch | null {
    const match = matchLevel(pathname);

    if (!match?.sidebarMatch) {
        return null;
    }

    const { parent, matched } = match.sidebarMatch;
    const isNested = parent.id !== matched.id;

    return {
        level: match.level,
        moduleItem: isNested ? parent : matched,
        subItem: isNested ? matched : undefined,
    };
}

/**
 * Resolves sidebar-derived page context from a URL pathname.
 * Used to enrich $pageview events with stable module/page identifiers.
 */
export function resolvePageContext(pathname: string): PageContext {
    const match = matchLevel(pathname);

    if (!match) {
        return { ...EMPTY_PAGE_CONTEXT };
    }

    if (match.sidebarMatch) {
        return {
            ...buildPageContext(match.sidebarMatch),
            navigation_level: match.level,
        };
    }

    const fallback = buildFallbackPageContextFromSuffix(match.suffix, match.level);
    return {
        ...EMPTY_PAGE_CONTEXT,
        ...fallback,
        navigation_level: match.level,
    };
}
